import { debugLogResponse } from './debugLogResponse';

export const getBackoffDelay = (
	currentDelay: number,
	maxDelay: number,
	retryAfterHeader: string | null | undefined,
	multiplier = 2
): number => {
	const retryAfterSeconds = retryAfterHeader ? Number(retryAfterHeader) : NaN;

	// If server tells us exactly how long to wait, use it
	if (!Number.isNaN(retryAfterSeconds) && retryAfterSeconds > 0) {
		return Math.min(maxDelay, retryAfterSeconds * 1000);
	}

	// Otherwise apply exponential backoff
	return Math.min(maxDelay, currentDelay * multiplier);
};

export const sleep = (ms: number): Promise<void> =>
	new Promise(resolve => setTimeout(resolve, ms));

export const refreshAccessToken = async (
	deliveryBase: string,
	idToken: string,
	refreshToken: string
): Promise<{ accessToken: string | null; response: any }> => {
	console.log('in refresh access token');
	const formData = new FormData();
	formData.append('client_id', idToken);
	formData.append('client_secret', refreshToken);
	formData.append('grant_type', 'client_credentials');

	const response = await fetch(`${deliveryBase}/api/v2/oauth/token`, {
		method: 'POST',
		headers: { 'X-Requested-With': 'XMLHttpRequest' },
		body: formData,
	});
	await debugLogResponse('delivery/oauth/token', response);
	console.log('response:', response);
	const body = await response.json();
	if (response.ok && body?.access_token) {
		const expiresAt = Date.now() + body.expires_in * 1000;
		localStorage.setItem('accesstoken', body.access_token);
		localStorage.setItem('accesstoken_expires_at', expiresAt.toString());
		return {
			accessToken: body.access_token as string,
			response: response,
		};
	}
	return { accessToken: null, response: response };
};

export async function fetchDeliveryAccessToken(
	deliveryBase: string,
	idToken: string,
	refreshToken: string,
	stopRef: { current: boolean },
	onStatus?: (status: number) => void,
	maxDelay: number = 15000
): Promise<string | null> {
	let delay = 3000;
	console.log('in fetch delivery access token');
	while (!stopRef.current) {
		const expiresAt = localStorage.getItem('accesstoken_expires_at');
		const isExpired = !expiresAt || Date.now() > Number(expiresAt);

		if (!isExpired && localStorage.getItem('accesstoken')) {
			console.log('token from ls');
			return localStorage.getItem('accesstoken');
		}

		const { accessToken, response } = await refreshAccessToken(
			deliveryBase,
			idToken,
			refreshToken
		);

		if (accessToken) {
			return accessToken;
		}
		// Handle specific statuses via callback
		if (onStatus) onStatus(response.status);

		if (response.status === 404 || response.status === 429) {
			delay = getBackoffDelay(
				delay,
				maxDelay,
				response.headers.get('Retry-After')
			);
			await sleep(delay);
			continue;
		}

		await sleep(delay);
	}

	return null;
}

type PollOptions<T> = {
	url: string;
	accessToken: string;

	stopRef: React.MutableRefObject<boolean>;

	getDelay: (
		prev: number,
		max: number,
		retryAfter?: string | null | undefined
	) => number;
	maxDelay: number;

	timerSet: (fn: () => void, delay: number) => void;

	on401?: () => Promise<string | null>; // refresh token handler

	onSuccess: (resp: Response, body?: any) => Promise<void> | void;

	shouldRetry?: (status: number) => boolean;

	parseBody?: (resp: Response) => Promise<any>;
};

export function createPollingLoop() {
	return async function poll<T>(opts: PollOptions<T>) {
		let accessToken = opts.accessToken;
		let delay = 3000;
		const MAX_DELAY = opts.maxDelay;
		console.log('in create polling loop');
		const run = async () => {
			if (opts.stopRef.current) return;

			const resp = await fetch(opts.url, {
				method: 'GET',
				headers: {
					'X-Requested-With': 'XMLHttpRequest',
					Authorization: `Bearer ${accessToken}`,
				},
			});
			console.log('resp;', resp);
			let body: any;
			if (opts.parseBody) {
				try {
					body = await opts.parseBody(resp);
				} catch {}
			}

			// SUCCESS
			if (resp.status === 200) {
				console.log('in success');
				await opts.onSuccess(resp, body);
				return;
			}

			// RETRY CASES (204/404/429 etc)
			if (
				opts.shouldRetry
					? opts.shouldRetry(resp.status)
					: [204, 404, 429].includes(resp.status)
			) {
				console.log('in retry');
				delay = opts.getDelay(
					delay,
					MAX_DELAY,
					resp.headers.get('Retry-After')
				);
				opts.timerSet(run, delay);
				return;
			}

			// AUTH REFRESH
			if (resp.status === 401 && opts.on401) {
				console.log('in auth refresh');
				const newToken = await opts.on401();
				if (newToken) {
					accessToken = newToken;
					opts.timerSet(run, 500);
					return;
				}
				return;
			}

			// fallback retry
			opts.timerSet(run, Math.min(MAX_DELAY, delay));
		};

		run();
	};
}

export type ContentsResponse = {
	RootContent?: string;
	Contents?: Array<{
		Uid?: string;
		ContentFieldValues?: {
			title?: {
				'de-DE'?: string;
				'en-GB'?: string;
			};
			vin?: string;
		};
	}>;
	Pagination?: {
		Total?: number;
	};
};
